import { useState, useEffect, useRef } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Plus, Trash2, Save, Send, Mail, Eye, X, ChevronUp, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const uid = () => Math.random().toString(36).slice(2, 10);

// ── Types ─────────────────────────────────────────────────────────────────────

type BlockType = "text" | "deals" | "events" | "food_stores" | "whats_on" | "news" | "venues" | "cta" | "divider";

type Block = {
  id: string; type: BlockType;
  content?: string; title?: string; limit?: number; town?: string;
  category?: string; activityType?: string; venueType?: string;
  ctaText?: string; ctaUrl?: string; ctaColor?: string;
  storeDealsOnly?: boolean;
};

type Template = {
  id: string; name: string; subject: string; blocks: Block[];
  createdAt: string; updatedAt?: string;
};

type CampaignJob = {id:string;requestKey:string;templateId:string;templateName:string;status:string;createdAt:string;lastErrorCode:string|null;total:number;sent:number;failed:number;skipped:number;review:number;waiting:number;processing:number};
const jobLabels:Record<string,string> = {queued:"Queued",sending:"Sending in the background",paused:"Sending paused",completed:"Finished",failed:"Finished with failures",needs_review:"Some delivery results need checking",cancelled:"Stopped"};

// ── Block metadata ────────────────────────────────────────────────────────────

const BLOCK_META: Record<BlockType, { icon: string; label: string; bg: string; desc: string }> = {
  text:        { icon: "✍️",  label: "Text",        bg: "#6366f1", desc: "Custom HTML — supports {{name}}" },
  deals:       { icon: "💰",  label: "Deals",       bg: "#10b981", desc: "Active discount offers from local businesses" },
  events:      { icon: "🎟️", label: "Events",      bg: "#f59e0b", desc: "Upcoming events across Ayrshire" },
  food_stores: { icon: "🍕",  label: "Food Order",  bg: "#ef4444", desc: "Food delivery & collection stores" },
  whats_on:    { icon: "🎸",  label: "What's On",   bg: "#8b5cf6", desc: "Pub & venue activities — live music, quiz nights…" },
  news:        { icon: "📰",  label: "News",        bg: "#0ea5e9", desc: "Latest Ayrshire news articles" },
  venues:      { icon: "🍺",  label: "Venues",      bg: "#f97316", desc: "Pubs, bars & venues" },
  cta:         { icon: "🔘",  label: "Button",      bg: "#1e3a5f", desc: "Call-to-action link button" },
  divider:     { icon: "─",   label: "Divider",     bg: "#94a3b8", desc: "Horizontal separator line" },
};

const BLOCK_ORDER: BlockType[] = ["text","deals","events","food_stores","whats_on","news","venues","cta","divider"];

const BLOCK_DEFAULTS: Record<BlockType, Partial<Block>> = {
  text:        { content: `<h2 style="color:#1e3a5f;margin:0 0 8px;">Hi {{name}},</h2>\n<p style="color:#374151;margin:0;">Write your message here…</p>` },
  deals:       { title: "Latest Deals & Discounts", limit: 4 },
  events:      { title: "Upcoming Events", limit: 4 },
  food_stores: { title: "Order Local", limit: 4 },
  whats_on:    { title: "What's On at Your Local", limit: 4 },
  news:        { title: "Latest Ayrshire News", limit: 3 },
  venues:      { title: "Discover Your Local", limit: 4 },
  cta:         { ctaText: "Visit Ayrshire Hub →", ctaUrl: "https://www.ayrshirehub.co.uk", ctaColor: "#f0a500" },
  divider:     {},
};

// ── Preset templates ──────────────────────────────────────────────────────────

const PRESETS: Array<{ name: string; subject: string; blocks: Block[] }> = [
  {
    name: "🍕 Order Local Tonight",
    subject: "Hi {{name}}, hungry? Order from local Ayrshire restaurants tonight 🍕",
    blocks: [
      { id: uid(), type: "text", content: `<h2 style="color:#1e3a5f;margin:0 0 8px;">Hi {{name}},</h2><p style="color:#374151;margin:0;">Skip the big apps — order local tonight and save up to 25% compared to Just Eat.</p>` },
      { id: uid(), type: "food_stores", title: "Order Local Tonight", limit: 6 },
      { id: uid(), type: "deals", title: "Food Shops with Deals On", limit: 4, storeDealsOnly: true },
      { id: uid(), type: "cta", ctaText: "Order Now →", ctaUrl: "https://www.ayrshirehub.co.uk/order", ctaColor: "#f0a500" },
    ],
  },
  {
    name: "☕ Breakfast & Lunch",
    subject: "Hi {{name}}, fancy breakfast or lunch today? ☕",
    blocks: [
      { id: uid(), type: "text", content: `<h2 style="color:#1e3a5f;margin:0 0 8px;">Good morning, {{name}}!</h2><p style="color:#374151;margin:0;">Local cafés, bakeries and sandwich shops — delivered to your door.</p>` },
      { id: uid(), type: "food_stores", title: "Breakfast & Lunch Near You", limit: 6, category: "Breakfast" },
      { id: uid(), type: "cta", ctaText: "Order Breakfast →", ctaUrl: "https://www.ayrshirehub.co.uk/order", ctaColor: "#f0a500" },
    ],
  },
  {
    name: "🎉 Weekend What's On",
    subject: "Hi {{name}}, here's what's on in Ayrshire this weekend 🎉",
    blocks: [
      { id: uid(), type: "text", content: `<h2 style="color:#1e3a5f;margin:0 0 8px;">Hi {{name}},</h2><p style="color:#374151;margin:0;">Don't miss the best events happening across Ayrshire this weekend.</p>` },
      { id: uid(), type: "events", title: "This Weekend's Events", limit: 4 },
      { id: uid(), type: "whats_on", title: "What's On at Your Local Pub", limit: 4 },
      { id: uid(), type: "news", title: "Latest Ayrshire News", limit: 3 },
    ],
  },
  {
    name: "🍺 Pub Night Out",
    subject: "Hi {{name}}, planning a night out in Ayrshire? 🍺",
    blocks: [
      { id: uid(), type: "text", content: `<h2 style="color:#1e3a5f;margin:0 0 8px;">Hi {{name}},</h2><p style="color:#374151;margin:0;">Quiz nights, live music, karaoke — your local pubs have the weekend sorted.</p>` },
      { id: uid(), type: "whats_on", title: "Live Entertainment This Week", limit: 6 },
      { id: uid(), type: "venues", title: "Discover Your Local", limit: 4 },
    ],
  },
  {
    name: "💰 Deals Roundup",
    subject: "Hi {{name}}, your Ayrshire deals & discounts roundup 💰",
    blocks: [
      { id: uid(), type: "text", content: `<h2 style="color:#1e3a5f;margin:0 0 8px;">Hi {{name}},</h2><p style="color:#374151;margin:0;">This week's best deals from local businesses — support local and save more.</p>` },
      { id: uid(), type: "deals", title: "This Week's Best Deals", limit: 6 },
      { id: uid(), type: "events", title: "Coming Up in Ayrshire", limit: 3 },
      { id: uid(), type: "cta", ctaText: "See All Deals →", ctaUrl: "https://www.ayrshirehub.co.uk/deals", ctaColor: "#1e3a5f" },
    ],
  },
  {
    name: "📰 Monthly Community Update",
    subject: "Hi {{name}}, your monthly update from Ayrshire Hub 📰",
    blocks: [
      { id: uid(), type: "text", content: `<h2 style="color:#1e3a5f;margin:0 0 8px;">Hi {{name}},</h2><p style="color:#374151;margin:0;">Here's everything happening in your community this month.</p>` },
      { id: uid(), type: "news", title: "Community News", limit: 4 },
      { id: uid(), type: "events", title: "Events This Month", limit: 4 },
      { id: uid(), type: "deals", title: "Live Deals", limit: 4 },
      { id: uid(), type: "divider" },
      { id: uid(), type: "cta", ctaText: "Visit Ayrshire Hub →", ctaUrl: "https://www.ayrshirehub.co.uk", ctaColor: "#1e3a5f" },
    ],
  },
];

// ── Block settings panel ──────────────────────────────────────────────────────

function BlockSettings({ block, onChange }: { block: Block; onChange: (u: Partial<Block>) => void }) {
  const field = (label: string, node: React.ReactNode) => (
    <div key={label}>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</label>
      {node}
    </div>
  );

  const input = (val: string, key: keyof Block, placeholder = "") => (
    <input value={val} onChange={(e) => onChange({ [key]: e.target.value || undefined })}
      placeholder={placeholder}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary transition-colors" />
  );

  const limitSelect = (val: number) => (
    <select value={val} onChange={(e) => onChange({ limit: Number(e.target.value) })}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary">
      {[2,3,4,6,8].map((n) => <option key={n} value={n}>{n} items</option>)}
    </select>
  );

  const commonFields = (b: Block) => [
    field("Section Title", input(b.title ?? "", "title", "e.g. Order Local Tonight")),
    field("Show up to", limitSelect(b.limit ?? 4)),
    field("Town filter (optional)", input(b.town ?? "", "town", "e.g. Kilmarnock")),
  ];

  if (block.type === "text") return (
    <div className="space-y-3">
      {field("Content (HTML)", (
        <textarea value={block.content ?? ""} onChange={(e) => onChange({ content: e.target.value })} rows={12}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono outline-none focus:border-primary resize-y leading-relaxed" />
      ))}
      <p className="text-xs text-slate-400">Use <code className="bg-slate-100 px-1 rounded">{"{{name}}"}</code> to personalise with recipient's first name.</p>
    </div>
  );

  if (block.type === "cta") return (
    <div className="space-y-3">
      {field("Button Text", input(block.ctaText ?? "", "ctaText", "Order Now →"))}
      {field("URL", input(block.ctaUrl ?? "", "ctaUrl", "https://www.ayrshirehub.co.uk"))}
      {field("Colour", (
        <div className="flex gap-2">
          {[["#f0a500","Gold"],["#1e3a5f","Navy"],["#10b981","Green"],["#ef4444","Red"]].map(([c, l]) => (
            <button key={c} title={l} onClick={() => onChange({ ctaColor: c })}
              style={{ background: c, width: 32, height: 32, borderRadius: 8, border: block.ctaColor === c ? "3px solid #1e3a5f" : "3px solid transparent", outline: "2px solid #e2e8f0" }} />
          ))}
        </div>
      ))}
    </div>
  );

  if (block.type === "divider") return <p className="text-sm text-slate-400">No settings for divider.</p>;

  return (
    <div className="space-y-3">
      {commonFields(block)}
      {block.type === "deals" && field("Data source", (
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={block.storeDealsOnly ?? false}
            onChange={(e) => onChange({ storeDealsOnly: e.target.checked || undefined, category: undefined })}
            className="w-4 h-4 accent-primary" />
          <span className="text-sm text-slate-700 font-medium">Order Local stores with active discounts only</span>
        </label>
      ))}
      {block.type === "deals" && !block.storeDealsOnly &&
        field("Category filter (optional)", input(block.category ?? "", "category", "e.g. Sport, Retail"))}
      {(block.type === "food_stores" || block.type === "news") &&
        field("Category filter (optional)", input(block.category ?? "", "category", block.type === "food_stores" ? "e.g. Breakfast, Pizza, Indian" : "e.g. Sport, Retail"))}
      {block.type === "events" &&
        field("Category filter (optional)", input(block.category ?? "", "category", "e.g. Music, Comedy, Sport"))}
      {block.type === "whats_on" && field("Activity type (optional)", (
        <select value={block.activityType ?? ""} onChange={(e) => onChange({ activityType: e.target.value || undefined })}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary">
          <option value="">All activities</option>
          <option value="live_music">Live Music</option>
          <option value="quiz_night">Quiz Night</option>
          <option value="karaoke">Karaoke</option>
          <option value="pool_table">Pool Table</option>
          <option value="jukebox">Jukebox</option>
          <option value="other">Other</option>
        </select>
      ))}
      {block.type === "venues" && field("Venue type (optional)", (
        <select value={block.venueType ?? ""} onChange={(e) => onChange({ venueType: e.target.value || undefined })}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary">
          <option value="">All venues</option>
          <option value="pub">Pub</option>
          <option value="bar">Bar</option>
          <option value="club">Club</option>
          <option value="restaurant">Restaurant</option>
        </select>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminCampaigns() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [activeBlockId, setActiveBlockId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dryRun, setDryRun] = useState<{ total: number; sample: string[] } | null>(null);
  const [sending, setSending] = useState(false);
  const [jobs,setJobs] = useState<CampaignJob[]>([]);
  const [progressError,setProgressError] = useState<string|null>(null);
  const sendInFlight = useRef(false);
  const activeJob = jobs.find(job=>["queued","sending","paused"].includes(job.status));
  useEffect(()=>{if(dirty)setDryRun(null);},[dirty,blocks,subject]);
  useEffect(()=>{
    if(!selectedId){setJobs([]);setProgressError(null);return;}
    let cancelled=false;
    const refresh=async()=>{try{
      const response=await fetch(`/api/admin/marketing-campaign-jobs?templateId=${encodeURIComponent(selectedId)}`,{headers:authHeaders()});
      const body=await response.json() as {jobs?:CampaignJob[];error?:string};
      if(!response.ok||!Array.isArray(body.jobs))throw Error(body.error??"Campaign progress is unavailable.");
      if(cancelled)return;setJobs(body.jobs);setProgressError(null);
      const key=`campaign-send:${selectedId}`,reference=sessionStorage.getItem(key);
      if(reference&&body.jobs.some(job=>job.requestKey===reference)){sessionStorage.removeItem(key);setDryRun(null);}
    }catch{if(!cancelled)setProgressError("Could not refresh campaign progress. Check again before starting another send.");}};
    void refresh();const interval=setInterval(()=>void refresh(),3000);return()=>{cancelled=true;clearInterval(interval);};
  },[selectedId]);


  const fetchTemplates = async () => {
    try {
      const res = await fetch("/api/admin/marketing-templates", { headers: authHeaders() });
      if (res.ok) setTemplates(await res.json() as Template[]);
    } catch { /* ignore */ } finally { setLoadingTemplates(false); }
  };
  useEffect(() => { void fetchTemplates(); }, []);

  const activeBlock = blocks.find((b) => b.id === activeBlockId) ?? null;

  const loadTemplate = (t: Template) => {
    setSelectedId(t.id); setName(t.name); setSubject(t.subject);
    setBlocks(t.blocks.map((b) => ({ ...b }))); setDirty(false);
    setActiveBlockId(null); setDryRun(null); setPreviewHtml(null);
  };

  const loadPreset = (p: (typeof PRESETS)[0]) => {
    setSelectedId(null); setName(p.name); setSubject(p.subject);
    setBlocks(p.blocks.map((b) => ({ ...b, id: uid() }))); setDirty(true);
    setActiveBlockId(null); setDryRun(null); setPreviewHtml(null);
  };

  const newTemplate = () => {
    setSelectedId(null); setName(""); setSubject(""); setBlocks([]); setDirty(false);
    setActiveBlockId(null); setDryRun(null); setPreviewHtml(null);
  };

  const addBlock = (type: BlockType) => {
    const b: Block = { id: uid(), type, ...BLOCK_DEFAULTS[type] };
    setBlocks((prev) => [...prev, b]);
    setActiveBlockId(b.id); setDirty(true);
  };

  const removeBlock = (id: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    if (activeBlockId === id) setActiveBlockId(null);
    setDirty(true);
  };

  const moveBlock = (id: string, dir: -1 | 1) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next]!, arr[idx]!];
      return arr;
    });
    setDirty(true);
  };

  const updateBlock = (id: string, updates: Partial<Block>) => {
    setBlocks((prev) => prev.map((b) => b.id === id ? { ...b, ...updates } : b));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast({ title: "Template name required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      const body = JSON.stringify({ name, subject, blocks });
      const res = selectedId
        ? await fetch(`/api/admin/marketing-templates/${selectedId}`, { method: "PUT", headers: authHeaders(), body })
        : await fetch("/api/admin/marketing-templates", { method: "POST", headers: authHeaders(), body });
      const t = await res.json() as Template;
      await fetchTemplates();
      setSelectedId(t.id); setDirty(false);
      toast({ title: "Template saved" });
    } catch { toast({ title: "Save failed", variant: "destructive" }); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedId || !confirm(`Delete "${name}"?`)) return;
    await fetch(`/api/admin/marketing-templates/${selectedId}`, { method: "DELETE", headers: authHeaders() });
    await fetchTemplates(); newTemplate();
    toast({ title: "Deleted" });
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch("/api/admin/marketing-templates/preview", { method: "POST", headers: authHeaders(), body: JSON.stringify({ blocks, subject }) });
      const d = await res.json() as { html: string };
      setPreviewHtml(d.html);
    } catch { toast({ title: "Preview failed", variant: "destructive" }); } finally { setPreviewLoading(false); }
  };

  const handleDryRun = async () => {
    if (!selectedId || dirty || activeJob) return;
    setDryRun(null);setSending(true);
    try {
      const res = await fetch(`/api/admin/marketing-templates/${selectedId}/send`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ dryRun: true }) });
      const body=await res.json() as {total:number;sample:string[];error?:string};
      if(!res.ok)throw Error(body.error??"Recipient preview failed");
      setDryRun(body);
    } catch(error) { toast({ title: (error as Error).message, variant: "destructive" }); } finally { setSending(false); }
  };

  const handleSend = async () => {
    if (!selectedId || !dryRun || dirty || activeJob || sendInFlight.current) return;
    if (!confirm(`Queue "${name}" for ${dryRun.total} users? Sending continues in the background.`)) return;
    sendInFlight.current=true;setSending(true);
    const key=`campaign-send:${selectedId}`;
    try {
      let requestKey=sessionStorage.getItem(key);if(!requestKey){requestKey=crypto.randomUUID();sessionStorage.setItem(key,requestKey);}
      const res=await fetch(`/api/admin/marketing-templates/${selectedId}/send`,{method:"POST",headers:authHeaders(),body:JSON.stringify({dryRun:false,requestKey})});
      const body=await res.json() as {job?:CampaignJob;error?:string};
      if(!res.ok||!body.job)throw Error(body.error??"The queue response was not confirmed. Check progress before retrying.");
      const job=body.job;setJobs(previous=>[job,...previous.filter(j=>j.id!==job.id)]);sessionStorage.removeItem(key);setDryRun(null);
      toast({title:job.status==="completed"?"Campaign finished":"Campaign queued",description:"Progress is saved. You can leave this page while it sends."});
    }catch(error){setDryRun(null);toast({title:"Campaign request not confirmed",description:(error as Error).message,variant:"destructive"});}
    finally{sendInFlight.current=false;setSending(false);}
  };
  const controlJob=async(job:CampaignJob,action:"resume"|"cancel")=>{
    if(!confirm(action==="resume"?"Resume only the unsent emails? Accepted emails and uncertain deliveries will not be sent again.":"Stop the remaining emails? An email already being sent may still finish."))return;
    try{const response=await fetch(`/api/admin/marketing-campaign-jobs/${job.id}/${action}`,{method:"POST",headers:authHeaders(),body:"{}"});const data=await response.json() as {job?:CampaignJob;error?:string};if(!response.ok||!data.job)throw Error(data.error??"Campaign update not confirmed");setJobs(previous=>previous.map(j=>j.id===job.id?data.job!:j));}
    catch(error){toast({title:(error as Error).message,variant:"destructive"});}
  };

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const blockSummary = (b: Block) => {
    if (b.type === "text") return (b.content ?? "").replace(/<[^>]+>/g, "").slice(0, 60) || "Empty text block";
    if (b.type === "cta") return `"${b.ctaText ?? "Button"}" → ${b.ctaUrl ?? ""}`;
    if (b.type === "divider") return "───────────────";
    if (b.type === "deals" && b.storeDealsOnly) return [b.title, b.limit ? `${b.limit} items` : null, "Order Local stores only", b.town].filter(Boolean).join(" • ");
    return [b.title, b.limit ? `${b.limit} items` : null, b.town, b.category, b.activityType, b.venueType].filter(Boolean).join(" • ");
  };

  return (
    <AdminLayout current="/admin/campaigns">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-primary mb-1">Email Campaign Creator</h1>
          <p className="text-sm text-slate-500">Build block-based emails with real data. Preview before sending.</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:shrink-0">
          <button onClick={() => void handlePreview()} disabled={previewLoading || !blocks.length}
            className="flex items-center gap-2 border border-slate-300 text-slate-700 font-semibold px-4 py-2 rounded-xl text-sm hover:bg-slate-50 transition-colors disabled:opacity-40">
            {previewLoading ? <span className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" /> : <Eye size={15} />} Preview
          </button>
          <button onClick={() => void handleSave()} disabled={saving || !dirty}
            className="flex items-center gap-2 bg-primary text-white font-semibold px-4 py-2 rounded-xl text-sm hover:bg-primary/90 transition-colors disabled:opacity-40">
            {saving ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={15} />}
            {selectedId ? "Save Changes" : "Save Template"}
          </button>
          {selectedId && (
            <button onClick={() => void handleDelete()} className="flex items-center gap-2 border border-red-200 text-red-600 font-semibold px-4 py-2 rounded-xl text-sm hover:bg-red-50 transition-colors">
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Name + Subject */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Template Name</label>
          <input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} placeholder="e.g. Weekend What's On"
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email Subject</label>
          <input value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true); }} placeholder="Hi {{name}}, here's what's on this weekend 🎉"
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors" />
        </div>
      </div>

      {/* Main 3-column layout */}
      <div className="flex flex-col lg:flex-row gap-5 items-start">

        {/* Left: saved templates + presets */}
        <div className="w-full lg:w-52 lg:shrink-0 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Saved</p>
              <button onClick={newTemplate} className="flex items-center gap-1 text-xs text-primary font-semibold hover:underline"><Plus size={12} /> New</button>
            </div>
            {loadingTemplates ? <p className="text-xs text-slate-400">Loading…</p> : templates.length === 0 ? <p className="text-xs text-slate-400">None saved yet</p> : (
              <div className="space-y-1.5">
                {templates.map((t) => (
                  <button key={t.id} onClick={() => loadTemplate(t)} className="w-full text-left px-3 py-2 rounded-xl border text-sm transition-colors truncate"
                    style={{ background: selectedId === t.id ? "#1e3a5f" : "#fff", borderColor: selectedId === t.id ? "#1e3a5f" : "#e2e8f0", color: selectedId === t.id ? "#fff" : "#374151" }}>
                    <p className="font-semibold text-xs truncate">{t.name}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Preset Starters</p>
            <div className="space-y-1.5">
              {PRESETS.map((p) => (
                <button key={p.name} onClick={() => loadPreset(p)} className="w-full text-left px-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-600 hover:border-primary hover:text-primary transition-colors font-medium truncate">
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Centre: block canvas */}
        <div className="flex-1 min-w-0 space-y-3">
          {blocks.length === 0 ? (
            <div className="border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center">
              <p className="text-slate-400 text-sm mb-1">No blocks yet</p>
              <p className="text-slate-300 text-xs">Add blocks below or choose a preset starter on the left</p>
            </div>
          ) : (
            blocks.map((b, i) => {
              const meta = BLOCK_META[b.type];
              const isActive = b.id === activeBlockId;
              return (
                <div key={b.id} onClick={() => setActiveBlockId(b.id)} className="cursor-pointer rounded-2xl border-2 transition-all"
                  style={{ borderColor: isActive ? meta.bg : "#e2e8f0", background: isActive ? "#fafafa" : "#fff" }}>
                  <div className="flex items-center gap-3 px-4 py-3">
                    <span className="text-lg shrink-0">{meta.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: meta.bg }}>{meta.label}</span>
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{blockSummary(b)}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); moveBlock(b.id, -1); }} disabled={i === 0} className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30">
                        <ChevronUp size={14} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); moveBlock(b.id, 1); }} disabled={i === blocks.length - 1} className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30">
                        <ChevronDown size={14} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); removeBlock(b.id); }} className="p-1 text-red-400 hover:text-red-600">
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {/* Add block palette */}
          <div className="pt-2">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Add Block</p>
            <div className="flex flex-wrap gap-2">
              {BLOCK_ORDER.map((type) => {
                const meta = BLOCK_META[type];
                return (
                  <button key={type} onClick={() => addBlock(type)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors hover:opacity-80"
                    style={{ background: meta.bg + "18", borderColor: meta.bg + "40", color: meta.bg }}>
                    <span>{meta.icon}</span> {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: block settings */}
        <div className="w-full lg:w-64 lg:shrink-0">
          {activeBlock ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-base">{BLOCK_META[activeBlock.type].icon}</span>
                <p className="font-bold text-sm text-primary">{BLOCK_META[activeBlock.type].label} Settings</p>
              </div>
              <p className="text-xs text-slate-400 -mt-2">{BLOCK_META[activeBlock.type].desc}</p>
              <BlockSettings block={activeBlock} onChange={(u) => updateBlock(activeBlock.id, u)} />
            </div>
          ) : (
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 text-center">
              <p className="text-sm text-slate-400 mb-1">Click a block to edit its settings</p>
              <p className="text-xs text-slate-300">Configure title, limit, town & category filters</p>
            </div>
          )}
        </div>
      </div>

      {/* Send section */}
      {selectedId && (
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6 space-y-4 max-w-xl">
          <h3 className="font-bold text-primary text-base">Send Campaign</h3>
          {dryRun && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700 space-y-1">
              <p className="font-semibold">Dry run complete:</p>
              <p>• <strong>{dryRun.total}</strong> users will receive this email</p>
              {dryRun.sample.length > 0 && <p className="text-xs text-blue-500">Sample: {dryRun.sample.join(", ")}</p>}
            </div>
          )}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs text-amber-700 font-medium">⚠️ Save changes and run a dry run to confirm the recipient count. Sending happens in the background, one email at a time.</p>
          </div>
          {progressError && <p role="alert" className="text-sm text-amber-800">{progressError}</p>}
          {dirty && <p className="text-sm text-amber-800">Save your changes before checking recipients or sending.</p>}
          <div className="space-y-3" aria-live="polite">{jobs.slice(0,5).map(job=><section key={job.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
            <div className="flex flex-wrap justify-between gap-2"><h4 className="font-semibold text-sm">{jobLabels[job.status]??job.status}</h4><span className="text-xs text-slate-500">{new Date(job.createdAt).toLocaleString("en-GB",{timeZone:"Europe/London"})}</span></div>
            <p className="text-sm">{job.sent} sent to the email server · {job.waiting+job.processing} remaining · {job.total} recipients</p>
            {(job.failed>0||job.review>0||job.skipped>0)&&<p className="text-xs text-slate-600">{job.failed} rejected · {job.review} uncertain · {job.skipped} skipped</p>}
            {job.status==="paused"&&<p className="text-xs text-amber-800">{job.lastErrorCode==="smtp_provider_limit"?"The email provider's sending limit was reached. Resume after the limit resets.":"The email provider rejected the login. Check the mail configuration before resuming."}</p>}
            {job.review>0&&<p className="text-xs text-amber-800">Uncertain emails are not retried automatically, to prevent duplicate messages.</p>}
            <p className="text-xs text-slate-500">Sent means accepted by the email server; inbox delivery is not confirmed. Progress is saved if you close this page.</p>
            <div className="flex gap-3">{job.status==="paused"&&<button className="text-xs font-semibold text-blue-700 underline" onClick={()=>void controlJob(job,"resume")}>Resume unsent emails</button>}{["queued","sending","paused"].includes(job.status)&&<button className="text-xs font-semibold text-red-700 underline" onClick={()=>void controlJob(job,"cancel")}>Stop remaining emails</button>}</div>
          </section>)}</div>
          <div className="flex gap-3">
            <button onClick={() => void handleDryRun()} disabled={sending || dirty || !!activeJob || !!progressError}
              className="flex-1 flex items-center justify-center gap-2 border border-primary text-primary font-semibold py-3 rounded-xl hover:bg-primary/5 transition-colors disabled:opacity-50">
              {sending && !dryRun ? <span className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /> : <Mail size={15} />} Dry Run
            </button>
            <button onClick={() => void handleSend()} disabled={sending || !dryRun || dirty || !!activeJob || !!progressError}
              className="flex-1 flex items-center justify-center gap-2 bg-primary text-white font-bold py-3 rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50">
              {sending && dryRun ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send size={15} />} Send Campaign
            </button>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewHtml && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-2xl shadow-2xl flex flex-col" style={{ width: "min(680px,100%)", maxHeight: "90vh" }}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
              <p className="font-bold text-primary">Email Preview</p>
              <button onClick={() => setPreviewHtml(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-2xl">
              <iframe srcDoc={previewHtml} title="Preview" className="w-full h-full border-0" style={{ minHeight: 520 }} sandbox="allow-same-origin" />
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
